import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useAuth } from "../../auth/AuthProvider.js";
import { sumIncentives } from "../../lib/api.js";
import { Badge, Button, Card, SelectionCard } from "../../components/index.js";
import type {
  FbaInventoryItem,
  PackingGroup,
  PackingOption,
  PlanItem,
  PrepDetailsItem
} from "../../types.js";
import type { BoxContentSku } from "../../lib/box-content.js";
import {
  isPackingConfirmedOrBeyond,
  isPackingInfoDone,
  isTerminalWorkflowStatus
} from "../../lib/plan-status.js";
import type { StepProps } from "./step-props.js";
import { labelOwnerLabel } from "./PackingModeloModal.js";
import { GrupoEmbalagemModal } from "./GrupoEmbalagemModal.js";

/**
 * Etapa 2 — Método e grupos de embalagem (Design.md §6.2).
 *
 * MÉTODOS: a Amazon devolve N packing options em `listPackingOptions`, cada uma
 * com 1..N `packingGroups` (modelo oficial `PackingOption`). NÃO existe campo
 * "recomendada": o sinal da recomendação é o DESCONTO ("Discount applied to the
 * fulfillment fee for adhering to Amazon recommendation"). Por isso TODAS as
 * opções são exibidas e a classificação usa o desconto — a heurística antiga
 * ("padrão = 1 grupo") escondia opções quando a Amazon não oferecia nenhuma de
 * grupo único (caso comum: 3 grupos com desconto vs 2 grupos sem desconto).
 *
 * GRUPOS: a opção confirmada pode ter mais de um grupo; cada grupo tem o seu
 * subconjunto de SKUs (lido da Amazon por `listPackingGroupItems`) e a sua
 * planilha de caixas. O avanço só libera quando TODOS os grupos foram enviados
 * e a Amazon aceitou o packing information.
 */
export function Step2Embalagem({
  planId,
  onReadyChange,
  onGoToStep
}: StepProps): ReactNode {
  const { api } = useAuth();
  const [options, setOptions] = useState<PackingOption[]>([]);
  const [items, setItems] = useState<PlanItem[]>([]);
  const [prep, setPrep] = useState<PrepDetailsItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [waitMsg, setWaitMsg] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [packingConfirmed, setPackingConfirmed] = useState(false);
  const [boxSubmitted, setBoxSubmitted] = useState(false);
  const [grupos, setGrupos] = useState<PackingGroup[]>([]);
  const [gruposCarregando, setGruposCarregando] = useState(false);
  // Erro REAL da consulta de grupos (antes era engolido) + gatilho de retry
  // manual, para o operador não depender de F5 quando o poll esgota.
  const [erroGrupos, setErroGrupos] = useState<string | null>(null);
  const [tentativaGrupos, setTentativaGrupos] = useState(0);
  const [grupoAberto, setGrupoAberto] = useState<PackingGroup | null>(null);
  const [planTerminal, setPlanTerminal] = useState<string | null>(null);
  // Motivo REAL da falha (mensagem da SP-API) — exibido em vez do genérico para o
  // operador não ficar cego ("falha silenciosa"). Vem de getWorkflowStatus.
  const [failureReason, setFailureReason] = useState<string | null>(null);
  // Recuperação do plano que falhou: reinicia o fluxo e devolve o operador ao
  // Passo 1 para corrigir os produtos (ex.: remover um SKU que a Amazon recusa).
  const [reiniciando, setReiniciando] = useState(false);
  const [erroReinicio, setErroReinicio] = useState<string | null>(null);
  // SKUs que a Amazon recusou (vêm do status) e se o plano ainda pode ser
  // reiniciado — quando já existe na Amazon, a saída é CLONAR sem esses SKUs.
  const [failureSkus, setFailureSkus] = useState<string[]>([]);
  const [canRestart, setCanRestart] = useState(false);
  // Snapshot de inventário por SKU p/ enriquecer imagem/título (req imagem).
  const [inventory, setInventory] = useState<Map<string, FbaInventoryItem>>(
    new Map()
  );

  // Inventário é best-effort (só enriquece o display): falha aqui não trava o Passo 2.
  useEffect(() => {
    let cancelled = false;
    api
      .getInventory()
      .then((r) => {
        if (!cancelled) {
          setInventory(new Map(r.items.map((it) => [it.sellerSku, it])));
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api]);

  // Bootstrap do Passo 2 (A5): dispara `startWorkflow` na entrada (idempotente —
  // no-op se já iniciado) e POLLA até as opções de embalagem aparecerem, parando
  // com mensagem pt-BR se o workflow falhar (FAILED/TIMED_OUT/CANCELLED) ou após
  // o timeout (não polla para sempre). Sem isto o workflow ficaria NOT_STARTED e
  // `getPackingOptions` voltaria vazio (Etapa 2 travada).
  useEffect(() => {
    let cancelled = false;
    const POLL_MS = 3000;
    const MAX_ATTEMPTS = 40; // ~2 min
    const TERMINAL = new Set(["FAILED", "TIMED_OUT", "CANCELLED"]);
    const sleep = (ms: number): Promise<void> =>
      new Promise((resolve) => setTimeout(resolve, ms));

    async function loadReady(): Promise<void> {
      const [optRes, itemsRes, prepRes] = await Promise.all([
        api.getPackingOptions(planId),
        api.getItems(planId),
        api.getPrepDetails(planId)
      ]);
      if (cancelled) return;
      setOptions(optRes.data.packingOptions);
      setItems(itemsRes.data.items);
      setPrep(prepRes.data.items);
      setCarregando(false);
      setWaitMsg(null);
    }

    function fail(message: string): void {
      if (cancelled) return;
      setErro(message);
      setCarregando(false);
      setWaitMsg(null);
    }

    async function bootstrap(): Promise<void> {
      setCarregando(true);
      setErro(null);
      setWaitMsg(null);
      try {
        const first = await api.getPackingOptions(planId);
        if (first.data.packingOptions.length > 0) {
          await loadReady();
          return;
        }
        if (cancelled) return;
        setWaitMsg("Iniciando o fluxo do plano…");
        await api.startWorkflow(planId);
        if (cancelled) return;
        setWaitMsg("Aguardando a Amazon gerar as opções de embalagem…");
        for (let attempt = 0; attempt < MAX_ATTEMPTS && !cancelled; attempt++) {
          await sleep(POLL_MS);
          if (cancelled) return;
          const optRes = await api.getPackingOptions(planId).catch(() => null);
          if (optRes !== null && optRes.data.packingOptions.length > 0) {
            await loadReady();
            return;
          }
          const status = await api.getWorkflowStatus(planId).catch(() => null);
          if (status !== null && TERMINAL.has(status.workflowStatus)) {
            const reason =
              typeof status.failureReason === "string" &&
              status.failureReason.length > 0
                ? status.failureReason
                : null;
            if (reason !== null) {
              setFailureReason(reason);
            }
            fail(
              reason !== null
                ? `O fluxo do plano falhou: ${reason}`
                : "O fluxo do plano falhou ao gerar as opções de embalagem. " +
                    "Verifique o plano e tente novamente."
            );
            return;
          }
        }
        if (!cancelled) {
          fail(
            "Tempo esgotado aguardando as opções de embalagem. " +
              "Atualize a página para tentar novamente."
          );
        }
      } catch (error: unknown) {
        fail(
          error instanceof Error ? error.message : "Falha ao iniciar o fluxo do plano."
        );
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [api, planId]);

  // CORREÇÃO do "Avançar" travado: a prontidão do Passo 2 é derivada do ESTADO
  // REAL do plano (poll de getWorkflowStatus), NÃO só do callback transitório do
  // modal. `boxSubmitted` (e `packingConfirmed`) passam a refletir o backend:
  //  - planStatus ∈ PACKING_INFO_DONE (setPackingInformation aceito) ⇒ Passo 2 ok.
  // Assim "Avançar" habilita mesmo que o modal feche antes do poll interno dele,
  // que o Cloud Tasks demore >24s, ou que a página seja revisitada/recarregada.
  // Plano terminal (CANCELLED/FAILED/TIMED_OUT) ⇒ mensagem clara, sem habilitar.
  useEffect(() => {
    let cancelled = false;
    const POLL_MS = 4000;
    const sleep = (ms: number): Promise<void> =>
      new Promise((resolve) => setTimeout(resolve, ms));

    async function track(): Promise<void> {
      for (;;) {
        if (cancelled) return;
        const status = await api.getWorkflowStatus(planId).catch(() => null);
        if (cancelled) return;
        if (status !== null) {
          if (isPackingConfirmedOrBeyond(status.planStatus)) {
            setPackingConfirmed(true);
          }
          if (isPackingInfoDone(status.planStatus)) {
            setBoxSubmitted(true);
            return; // Passo 2 concluído — para de pollar.
          }
          if (isTerminalWorkflowStatus(status.workflowStatus)) {
            setPlanTerminal(status.workflowStatus);
            if (
              typeof status.failureReason === "string" &&
              status.failureReason.length > 0
            ) {
              setFailureReason(status.failureReason);
            }
            setFailureSkus(status.failureSkus ?? []);
            setCanRestart(status.canRestart === true);
            return; // Plano cancelado/falhou — para de pollar.
          }
        }
        await sleep(POLL_MS);
      }
    }

    void track();
    return () => {
      cancelled = true;
    };
  }, [api, planId]);

  const carregarGrupos = useCallback(async (): Promise<PackingGroup[]> => {
    try {
      const res = await api.getPackingGroups(planId);
      setErroGrupos(null);
      setGrupos(res.data.groups);
      return res.data.groups;
    } catch (error: unknown) {
      // Antes o erro real era descartado (`.catch(() => null)`) e a tela dizia
      // apenas "ainda não disponíveis": falha de rede/sessão ficava invisível.
      setErroGrupos(
        error instanceof Error ? error.message : "Falha ao consultar os grupos."
      );
      return [];
    }
  }, [api, planId]);

  // Grupos da opção confirmada: o LIST roda em uma task (assíncrono), então
  // polla até aparecerem. Sem grupos o operador não sabe quais SKUs vão em cada
  // planilha.
  useEffect(() => {
    if (!packingConfirmed) {
      return;
    }
    let cancelled = false;
    const sleep = (ms: number): Promise<void> =>
      new Promise((resolve) => setTimeout(resolve, ms));

    async function poll(): Promise<void> {
      setGruposCarregando(true);
      for (let attempt = 0; attempt < 30 && !cancelled; attempt++) {
        const found = await carregarGrupos();
        if (cancelled) return;
        // Pronto só quando TODOS os grupos já têm seus itens (o LIST por grupo
        // roda em task; antes disso a planilha sairia vazia).
        if (found.length > 0 && found.every((g) => g.items.length > 0)) {
          setGruposCarregando(false);
          return;
        }
        await sleep(3000);
      }
      if (!cancelled) {
        setGruposCarregando(false);
      }
    }

    void poll();
    return () => {
      cancelled = true;
    };
    // `tentativaGrupos` permite re-disparar o poll pelo botão "Tentar novamente".
  }, [packingConfirmed, carregarGrupos, tentativaGrupos]);

  // Classificação dos métodos (ver cabeçalho): a recomendação da Amazon é
  // sinalizada pelo DESCONTO. O PADRÃO FiCC é a opção de MAIOR benefício líquido
  // (descontos − tarifas) — ou seja, a recomendada pela Amazon quando ela existe.
  const recommendedIds = new Set(
    options.filter((o) => sumIncentives(o.discounts) > 0).map((o) => o.packingOptionId)
  );
  const beneficioLiquido = (o: PackingOption): number =>
    sumIncentives(o.discounts) - sumIncentives(o.fees);
  const padrao =
    [...options].sort((a, b) => {
      const diff = beneficioLiquido(b) - beneficioLiquido(a);
      if (diff !== 0) return diff;
      // Empate (ex.: nenhuma opção com desconto): menos grupos = menos divisões.
      return a.packingGroups.length - b.packingGroups.length;
    })[0] ?? null;
  // Padrão FiCC primeiro, depois as demais em ordem crescente de nº de grupos.
  const ordenadas = [...options].sort((a, b) => {
    if (a.packingOptionId === padrao?.packingOptionId) return -1;
    if (b.packingOptionId === padrao?.packingOptionId) return 1;
    return a.packingGroups.length - b.packingGroups.length;
  });
  const descontoPadrao = padrao !== null ? sumIncentives(padrao.discounts) : 0;

  // Pré-seleciona o método padrão = o de maior desconto da Amazon.
  useEffect(() => {
    if (selectedId === null && padrao !== null) {
      setSelectedId(padrao.packingOptionId);
    }
  }, [selectedId, padrao]);

  useEffect(() => {
    onReadyChange(packingConfirmed && boxSubmitted);
  }, [packingConfirmed, boxSubmitted, onReadyChange]);

  const prepByMsku = new Map(prep.map((p) => [p.msku, p]));
  const itemByMsku = new Map(items.map((i) => [i.msku, i]));

  /** SKUs de um grupo, enriquecidos com prep/inventário para exibição. */
  function skusDoGrupo(group: PackingGroup): BoxContentSku[] {
    return group.items.map((groupItem) => {
      const p = prepByMsku.get(groupItem.msku) ?? null;
      const inv = inventory.get(groupItem.msku) ?? null;
      const planItem = itemByMsku.get(groupItem.msku) ?? null;
      return {
        sku: groupItem.msku,
        title: inv?.productName || groupItem.msku,
        asin: groupItem.asin || p?.asin || inv?.asin || "",
        fnsku: groupItem.fnsku || planItem?.fnsku || p?.fnsku || "",
        imageUrl: inv?.imageUrl ?? "",
        prepType:
          groupItem.prepTypes.length > 0
            ? groupItem.prepTypes.join(", ")
            : (p?.prepTypes.join(", ") ?? ""),
        preparedBy: labelOwnerLabel(p?.prepOwner ?? planItem?.prepOwner ?? null),
        labeledBy: labelOwnerLabel(
          groupItem.labelOwner || p?.labelOwner || planItem?.labelOwner || null
        ),
        expectedQuantity: groupItem.quantity
      };
    });
  }

  const gruposPendentes = grupos.filter((g) => g.boxConfig === null);

  /**
   * Saída do beco sem saída. Dois caminhos, escolhidos pelo estado REAL:
   *  - `canRestart` (nada foi registrado na Amazon): reinicia este mesmo plano e
   *    volta ao Passo 1 para corrigir os produtos.
   *  - caso contrário (o plano já existe na Amazon — o normal quando o
   *    createInboundPlan recusa um SKU): cria um NOVO envio com os mesmos
   *    produtos, já SEM os SKUs recusados, e abre esse novo plano.
   */
  async function recuperarPlano(): Promise<void> {
    setReiniciando(true);
    setErroReinicio(null);
    try {
      if (canRestart) {
        await api.restartWorkflow(planId);
        if (onGoToStep !== undefined) {
          onGoToStep(0);
        } else {
          window.location.reload();
        }
        return;
      }
      const novo = await api.clonePlan(planId, failureSkus);
      // Abre o novo plano no Passo 1 (o wizard lê o planId da querystring).
      window.location.href = `${window.location.pathname}?planId=${novo.planId}`;
    } catch (error: unknown) {
      setErroReinicio(
        error instanceof Error
          ? error.message
          : "Falha ao preparar a correção do plano."
      );
    } finally {
      setReiniciando(false);
    }
  }

  async function confirmarMetodo(): Promise<void> {
    if (selectedId === null) {
      return;
    }
    setConfirming(true);
    setErro(null);
    try {
      await api.confirmPackingOption(planId, selectedId);
      setPackingConfirmed(true);
    } catch (error: unknown) {
      setErro(error instanceof Error ? error.message : "Falha ao confirmar o método.");
    } finally {
      setConfirming(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div
        style={{
          border: "1px solid var(--amber)",
          background: "rgba(154,107,18,0.08)",
          borderRadius: 9,
          padding: "0.75rem",
          fontSize: 12.5,
          color: "var(--amber)",
          fontWeight: 600
        }}
      >
        Método padrão FICC: o <strong>método recomendado pela Amazon</strong> — é
        o que traz o desconto na tarifa de fulfillment
        {descontoPadrao > 0 ? ` (${formatMoney(descontoPadrao)} neste plano)` : ""}
        . Escolher outro método <strong>abre mão desse desconto</strong>; só faça
        isso com autorização do Admin.
      </div>

      {planTerminal !== null ? (
        <Card>
          <p role="alert" style={{ color: "var(--orange)", margin: 0 }}>
            {planTerminal === "CANCELLED"
              ? "Este plano foi cancelado — não é possível avançar. Crie um novo plano de envio."
              : "O fluxo deste plano falhou — não é possível avançar nesta etapa."}
          </p>
          {planTerminal !== "CANCELLED" ? (
            <p
              style={{
                color: "var(--text)",
                margin: "0.5rem 0 0",
                fontSize: 12.5
              }}
            >
              <strong>Motivo:</strong>{" "}
              {failureReason ??
                "a Amazon não detalhou a causa. Consulte o histórico do plano ou acione um Admin."}
            </p>
          ) : null}
          {planTerminal !== "CANCELLED" ? (
            <div style={{ marginTop: "0.75rem" }}>
              <p
                style={{
                  color: "var(--text-muted)",
                  margin: "0 0 0.5rem",
                  fontSize: 12.5
                }}
              >
                {canRestart
                  ? "Nada foi registrado na Amazon: corrija os produtos no Passo 1 e reinicie este mesmo envio — os itens cadastrados são mantidos."
                  : failureSkus.length > 0
                    ? `Este plano já existe na Amazon e não pode ser reiniciado (reiniciar o duplicaria lá). Crie um novo envio com os mesmos produtos, sem ${failureSkus.join(", ")}.`
                    : "Este plano já existe na Amazon e não pode ser reiniciado. Crie um novo envio com os mesmos produtos e ajuste o que for necessário no Passo 1."}
              </p>
              <Button onClick={() => void recuperarPlano()} disabled={reiniciando}>
                {reiniciando
                  ? "Preparando…"
                  : canRestart
                    ? "Corrigir produtos e reiniciar envio"
                    : failureSkus.length > 0
                      ? `Criar novo envio sem ${failureSkus.join(", ")}`
                      : "Criar novo envio com estes produtos"}
              </Button>
              {erroReinicio !== null ? (
                <p
                  role="alert"
                  style={{ color: "var(--orange)", margin: "0.5rem 0 0", fontSize: 12.5 }}
                >
                  {erroReinicio}
                </p>
              ) : null}
            </div>
          ) : null}
        </Card>
      ) : null}

      {erro !== null ? <p role="alert" style={{ color: "var(--orange)" }}>{erro}</p> : null}
      {carregando ? (
        <p>{waitMsg ?? "Carregando opções de embalagem…"}</p>
      ) : null}

      {!carregando && erro === null && options.length === 0 ? (
        <Card>
          <p style={{ color: "var(--text-muted)" }}>
            As opções de embalagem ainda não foram geradas pela Amazon.
          </p>
          {/* Antes só sugeria F5 — que não reinicia nada. Recarregar o wizard
              re-executa o bootstrap (startWorkflow é idempotente), então damos o
              controle explícito em vez de uma instrução vaga. */}
          <div style={{ marginTop: "0.6rem" }}>
            <Button variant="secondary" onClick={() => window.location.reload()}>
              Verificar novamente
            </Button>
          </div>
        </Card>
      ) : null}

      {options.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "1rem"
          }}
        >
          {ordenadas.map((option) => {
            const isPadrao = option.packingOptionId === padrao?.packingOptionId;
            const isRecomendada = recommendedIds.has(option.packingOptionId);
            const desconto = sumIncentives(option.discounts);
            const tarifa = sumIncentives(option.fees);
            return (
              <SelectionCard
                key={option.packingOptionId}
                selected={selectedId === option.packingOptionId}
                onSelect={() => setSelectedId(option.packingOptionId)}
                disabled={packingConfirmed}
                title={
                  isRecomendada
                    ? "Método recomendado pela Amazon"
                    : "Método alternativo (sem desconto)"
                }
                {...(isPadrao
                  ? { badge: <Badge tone="orange">PADRÃO FICC</Badge> }
                  : {})}
              >
                {option.packingGroups.length}{" "}
                {option.packingGroups.length === 1 ? "grupo" : "grupos"} de embalagem
                {desconto > 0
                  ? ` · desconto estimado ${formatMoney(desconto)}`
                  : " · sem desconto"}
                {tarifa > 0 ? ` · tarifa ${formatMoney(tarifa)}` : ""}
                {!isPadrao && descontoPadrao > 0 ? (
                  <span style={{ display: "block", marginTop: "0.25rem", color: "var(--amber)" }}>
                    Abre mão de {formatMoney(descontoPadrao - desconto)} de desconto
                  </span>
                ) : null}
              </SelectionCard>
            );
          })}
        </div>
      ) : null}

      {options.length > 0 ? (
        <div style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
          {packingConfirmed ? (
            <Badge tone="green">✓ Método confirmado</Badge>
          ) : (
            <Button onClick={() => void confirmarMetodo()} disabled={confirming || selectedId === null}>
              {confirming ? "Confirmando…" : "Confirmar método"}
            </Button>
          )}
        </div>
      ) : null}

      {packingConfirmed && grupos.length === 0 ? (
        <Card>
          <p style={{ color: "var(--text-muted)", fontSize: 12.5, margin: 0 }}>
            {gruposCarregando
              ? "Carregando os grupos de embalagem confirmados pela Amazon…"
              : erroGrupos !== null
                ? `Não foi possível carregar os grupos de embalagem: ${erroGrupos}`
                : "Os grupos de embalagem ainda não foram devolvidos pela Amazon."}
          </p>
          {!gruposCarregando ? (
            <div style={{ marginTop: "0.6rem" }}>
              <Button
                variant="secondary"
                onClick={() => setTentativaGrupos((n) => n + 1)}
              >
                Tentar novamente
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {packingConfirmed && grupos.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.7rem" }}>
          {grupos.length > 1 ? (
            <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
              Este método divide o envio em {grupos.length} grupos de embalagem —
              cada grupo tem sua própria planilha de caixas.{" "}
              <strong>
                {grupos.length - gruposPendentes.length} de {grupos.length} enviados
              </strong>
              .
            </div>
          ) : null}
          {grupos.map((group) => {
            const skus = skusDoGrupo(group);
            const unidades = skus.reduce((a, s) => a + s.expectedQuantity, 0);
            return (
              <Card
                key={group.packingGroupId}
                title={`Grupo de embalagem ${group.index}`}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "0.75rem"
                  }}
                >
                  <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                    {group.boxConfig !== null
                      ? `${group.boxConfig.boxCount} caixas · planilha importada`
                      : `${skus.length} SKUs · ${unidades} unidades`}
                    {group.boxConfig !== null ? (
                      <Badge tone="green" style={{ marginLeft: "0.5rem" }}>
                        caixas enviadas
                      </Badge>
                    ) : null}
                  </div>
                  <Button
                    variant="secondary"
                    disabled={skus.length === 0}
                    onClick={() => setGrupoAberto(group)}
                  >
                    {skus.length === 0
                      ? "Carregando itens do grupo…"
                      : "Abrir planilha do grupo"}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      ) : null}

      {grupoAberto !== null ? (
        <GrupoEmbalagemModal
          open
          onClose={() => setGrupoAberto(null)}
          planId={planId}
          packingGroupId={grupoAberto.packingGroupId}
          groupLabel={`Grupo de embalagem ${grupoAberto.index}`}
          skus={skusDoGrupo(grupoAberto)}
          // Espera a Amazon aceitar o packing information só quando, após ESTE
          // envio, não sobra nenhum grupo pendente; com grupos faltando o
          // backend segue aguardando (não adianta pollar até PACKING_INFO_SET).
          waitForAmazon={gruposPendentes.every(
            (g) => g.packingGroupId === grupoAberto.packingGroupId
          )}
          onSubmitted={() => {
            void carregarGrupos();
          }}
        />
      ) : null}
    </div>
  );
}

function formatMoney(amount: number): string {
  return amount.toLocaleString("pt-BR", { style: "currency", currency: "GBP" });
}
